<template>
  <div class="page">
    <!-- Topbar -->
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark">F</div>
        <div class="brand-text">
          <span class="brand-name">falcon</span>
          <span class="brand-sub">BACKOFFICE</span>
        </div>
      </div>
      <div class="support-links">
        <span class="support-label">Precisa de ajuda?</span>
        <a href="mailto:suporte@falcon.com.br" class="support-cta">Falar com o suporte</a>
      </div>
    </header>

    <!-- Login card -->
    <main class="card-wrapper">
      <div class="login-card">
        <p class="eyebrow">SUA VEZ DE VOAR.</p>
        <h1 class="heading">Backoffice</h1>
        <p class="subheading">Use suas credenciais de administrador<br>para continuar.</p>

        <form @submit.prevent="handleLogin" class="login-form" novalidate>
          <!-- Username -->
          <div class="field">
            <label for="username" class="field-label">Usuário ou e-mail</label>
            <div class="input-wrap">
              <svg class="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2"/>
                <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <input
                id="username"
                v-model="credentials.username"
                type="text"
                class="form-input"
                placeholder="piloto@falcon.com.br"
                required
                autocomplete="username"
              />
            </div>
          </div>

          <!-- Password -->
          <div class="field">
            <div class="field-label-row">
              <label for="password" class="field-label">Senha</label>
              <a href="#" @click.prevent="showPasswordRecovery" class="forgot-link">Esqueceu a senha?</a>
            </div>
            <div class="input-wrap">
              <svg class="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" stroke-width="2"/>
                <path d="M8 11V7a4 4 0 1 1 8 0v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <input
                id="password"
                v-model="credentials.password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input password-input"
                placeholder="••••••••"
                required
                autocomplete="current-password"
              />
              <button
                type="button"
                @click="togglePasswordVisibility"
                class="password-toggle"
                :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
              >
                <svg v-if="showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2.458 12C3.732 7.943 7.523 5 12 5C16.478 5 20.268 7.943 21.542 12C20.268 16.057 16.478 19 12 19C7.523 19 3.732 16.057 2.458 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Remember session -->
          <label class="remember-row">
            <input type="checkbox" v-model="rememberSession" class="remember-checkbox" />
            <span class="remember-label">Manter sessão por 7 dias</span>
          </label>

          <!-- Submit -->
          <button type="submit" class="login-button" :disabled="loading">
            <span v-if="loading">Entrando...</span>
            <span v-else>ENTRAR</span>
          </button>

          <!-- Error -->
          <div v-if="displayError" class="error-message" role="alert">
            {{ displayError }}
          </div>
        </form>

        <!-- Separator -->
        <div class="separator">
          <span class="separator-line"></span>
          <span class="separator-text">ou</span>
          <span class="separator-line"></span>
        </div>

        <!-- Google sign-in (in development) -->
        <button type="button" class="google-button" disabled aria-label="Login com Google (em desenvolvimento)">
          <span class="google-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          </span>
          <span>Entrar com Google</span>
        </button>
        <p class="google-note">Login com Google em desenvolvimento</p>

        <!-- Info block -->
        <div class="info-block" role="note">
          <svg class="info-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <div class="info-text">
            <p>Acesso restrito a administradores e equipe operacional autorizada.</p>
            <p>Contate o suporte para solicitar acesso.</p>
          </div>
        </div>

        <!-- Card footer -->
        <footer class="card-footer">
          <span>falcon · backoffice - {{ appVersion }}</span>
          <span>ambiente prod-br</span>
        </footer>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth, hasPendingVerification, mustChangePassword } from '@/composables/useAuth'
import { appVersion } from '@/config/appVersion'
import { logger } from '@/utils/logger'

const router = useRouter()
const { login, error, loading, clearError, isLoggedIn } = useAuth()

const credentials = reactive({ username: '', password: '' })
const showPassword = ref(false)
const rememberSession = ref(false)
const submitError = ref('')
const displayError = computed(() => error.value || submitError.value)

onMounted(() => {
  logger.componentMounted('LoginView')
  if (isLoggedIn.value) {
    const target = hasPendingVerification.value
      ? '/verification'
      : mustChangePassword.value
        ? '/change-password'
        : '/dashboard'
    logger.info(`🔄 User already authenticated, redirecting to ${target}`)
    router.push(target)
  }
})

const handleLogin = async () => {
  if (!credentials.username || !credentials.password) return
  clearError()
  submitError.value = ''
  try {
    await login({
      username: credentials.username,
      password: credentials.password,
      rememberSession: rememberSession.value,
    })
  } catch (err) {
    submitError.value = err instanceof Error ? err.message : 'Erro inesperado durante o login. Tente novamente.'
    logger.error('Login failed:', err)
  }
}

const showPasswordRecovery = () => {
  logger.info('Password recovery requested from login page')
  window.open('mailto:suporte@falcon.com.br?subject=Recuperação%20de%20senha%20Falcon', '_self')
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────── */
.page {
  min-height: 100vh;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  font-family: 'Segoe UI', Arial, sans-serif;
}

/* ── Topbar ───────────────────────────────────────── */
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  height: 60px;
  background-color: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-mark {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: #e53e3e;
  color: white;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.brand-name {
  font-size: 16px;
  font-weight: 700;
  color: #2d3748;
}

.brand-sub {
  font-size: 9px;
  font-weight: 700;
  color: #a0aec0;
  letter-spacing: 3px;
}

.support-links {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.support-label {
  font-size: 12px;
  color: #718096;
}

.support-cta {
  font-size: 12px;
  font-weight: 600;
  color: #e53e3e;
  text-decoration: none;
}

.support-cta:hover {
  text-decoration: underline;
}

/* ── Card wrapper ─────────────────────────────────── */
.card-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 20px 60px;
}

.login-card {
  width: 440px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 56px 48px 40px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Card header ──────────────────────────────────── */
.eyebrow {
  font-size: 12px;
  font-weight: 700;
  color: #e53e3e;
  letter-spacing: 2px;
  text-align: center;
  margin: 0 0 12px;
}

.heading {
  font-size: 26px;
  font-weight: 700;
  color: #2d3748;
  text-align: center;
  margin: 0 0 8px;
}

.subheading {
  font-size: 14px;
  color: #4a5568;
  text-align: center;
  line-height: 1.5;
  margin: 0 0 32px;
}

/* ── Form ─────────────────────────────────────────── */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
}

.field-label-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.forgot-link {
  font-size: 12px;
  color: #e53e3e;
  font-weight: 600;
  text-decoration: none;
}

.forgot-link:hover {
  text-decoration: underline;
}

.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #a0aec0;
  pointer-events: none;
}

.form-input {
  width: 100%;
  padding: 12px 12px 12px 38px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background-color: #ffffff;
  color: #2d3748;
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.form-input:focus {
  outline: none;
  border-color: #e53e3e;
  box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
}

.form-input::placeholder {
  color: #a0aec0;
}

.password-input {
  padding-right: 44px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.15s;
}

.password-toggle:hover {
  color: #4a5568;
}

/* ── Remember checkbox ────────────────────────────── */
.remember-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-top: 4px;
}

.remember-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #e53e3e;
  cursor: pointer;
  flex-shrink: 0;
}

.remember-label {
  font-size: 13px;
  color: #4a5568;
}

/* ── Submit button ────────────────────────────────── */
.login-button {
  background-color: #e53e3e;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.1s;
  margin-top: 4px;
}

.login-button:hover:not(:disabled) {
  background-color: #c53030;
  transform: translateY(-1px);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  background-color: #a0aec0;
  cursor: not-allowed;
}

/* ── Error ────────────────────────────────────────── */
.error-message {
  background-color: #fed7d7;
  color: #c53030;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
}

/* ── Separator ────────────────────────────────────── */
.separator {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 16px;
}

.separator-line {
  flex: 1;
  height: 1px;
  background-color: #e2e8f0;
}

.separator-text {
  font-size: 12px;
  color: #a0aec0;
  flex-shrink: 0;
}

/* ── Google button ────────────────────────────────── */
.google-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 13px;
  background-color: #f7fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  cursor: not-allowed;
  opacity: 0.7;
}

.google-icon {
  display: flex;
  align-items: center;
}

.google-note {
  font-size: 11px;
  color: #a0aec0;
  text-align: center;
  margin: 8px 0 0;
}

/* ── Info block ───────────────────────────────────── */
.info-block {
  display: flex;
  gap: 10px;
  background-color: rgba(66, 153, 225, 0.1);
  border: 1px solid rgba(66, 153, 225, 0.25);
  border-radius: 8px;
  padding: 14px;
  margin-top: 24px;
  align-items: flex-start;
}

.info-icon {
  color: #4299e1;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-text p {
  margin: 0;
  font-size: 12px;
  color: #2b6cb0;
  line-height: 1.5;
}

/* ── Card footer ──────────────────────────────────── */
.card-footer {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #e2e8f0;
  padding-top: 16px;
  margin-top: 24px;
  font-size: 12px;
  color: #a0aec0;
}

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 520px) {
  .topbar {
    padding: 0 20px;
  }

  .card-wrapper {
    padding: 20px 16px 40px;
  }

  .login-card {
    width: 100%;
    padding: 40px 24px 32px;
  }
}
</style>
